import moment from 'moment-timezone';
import { ConsumptionData, ConsumptionPeriod, IntervalType } from 'model/Consumption';
import UnitSystem, { ConverterFn, UnitConverter, UnitFormat } from 'model/UnitSystem';

type IntervalValues = '1h' | '1d' | '1m';

export interface ConsumptionParameters {
  startDate: string;
  endDate: string;
  locationId?: string | string[];
  macAddress?: string;
  interval: IntervalValues;
  tz?: string;
  userId?: string;
}

interface ConsumptionItemResponse {
  gallonsConsumed: number | null;
  time: string;
}

interface ConsumptionAggregationsResponse {
  sumTotalGallonsConsumed: number;
}

export interface ConsumptionResponse {
  items: ConsumptionItemResponse[];
  aggregations: ConsumptionAggregationsResponse;
  params: ConsumptionParameters;
}

interface MetricsItemResponse {
  time: string;
  averagePsi: number;
  averageGpm: number;
  averageTempF: number;
  averageWeatherTempF: number;
}

export interface MetricsResponse {
  items: MetricsItemResponse[];
  aggregations: ConsumptionAggregationsResponse;
}

const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSS';

export function getSettingsByPeriod(period: ConsumptionPeriod, tz: string, previous: boolean = false): ConsumptionParameters {
  switch (period) {
    case ConsumptionPeriod.TODAY: {
      const start = previous ? moment().subtract(24, 'hours') : moment();
      const end = previous ? moment().subtract(24, 'hours') : moment();
      return {
        startDate: start.startOf('day').format(dateFormat),
        endDate: end.endOf('day').format(dateFormat),
        interval: '1h',
      }
    }
    case ConsumptionPeriod.LAST_24_HOURS: {
      const start = previous ? moment().tz(tz).subtract(48, 'hours') : moment().tz(tz).subtract(24, 'hours');
      const end = previous ? moment().tz(tz).subtract(24, 'hours') : moment().tz(tz);
      return {
        startDate: start.startOf('hour').format(dateFormat),
        endDate: end.startOf('hour').format(dateFormat),
        interval: '1h',
      }
    }
    case ConsumptionPeriod.THIS_WEEK: {
      const start = previous ? moment().subtract(7, 'days').startOf('week') : moment().startOf('week');
      const end = previous ? moment().subtract(7, 'days').endOf('week') : moment().endOf('week');
      const today = previous ? moment().subtract(7, 'days') : moment();
      start.subtract(1, 'day');
      end.subtract(1, 'day');
      const isBetween = today.isBetween(start.clone(), end.clone());
      return {
        startDate: isBetween ? start.format(dateFormat) : start.add(1, 'week').format(dateFormat),
        endDate: isBetween ? end.format(dateFormat) : end.add(1, 'week').format(dateFormat),
        interval: '1d',
      }
    }
    case ConsumptionPeriod.LAST_28_DAYS: {
      const start = previous ? moment().subtract(56, 'days') : moment().subtract(28, 'days');
      const end = previous ? moment().subtract(28, 'days') : moment();
      return {
        startDate: start.add(1, 'days').startOf('day').format(dateFormat),
        endDate: end.endOf('day').format(dateFormat),
        interval: '1d',
      }
    }
    case ConsumptionPeriod.LAST_12_MONTHS: {
      const start = previous ? moment().subtract(14, 'months') : moment().subtract(12, 'months');
      const end = previous ? moment().subtract(12, 'months') : moment();
      return {
        startDate: start.add(1, 'month').startOf('month').format('YYYY-MM-DD'),
        endDate: end.endOf('month').format('YYYY-MM-DD'),
        interval: '1m',
      }
    }
    case ConsumptionPeriod.LAST_HOUR: {
      const start = previous ? moment().tz(tz).subtract(2, 'hours') : moment().tz(tz).subtract(1, 'hours');
      const end = previous ? moment().tz(tz).subtract(1, 'hours') : moment().tz(tz);
      return {
        startDate: start.startOf('hour').format(dateFormat),
        endDate: end.startOf('hour').format(dateFormat),
        interval: '1h',
      }
    }
  }
}

const convertMeasure = (unit: UnitSystem, value: number | undefined | null, converter: ConverterFn): number => {
  return Math.round(converter(value || 0, unit) * 10) / 10;
}

export function toConsumptionData(data: ConsumptionResponse, unitSystem: UnitSystem, timezone: string): ConsumptionData {
  return {
    items: data.items.map(datum => ({
      time: datum.time,
      value: convertMeasure(unitSystem, datum.gallonsConsumed, UnitConverter.convertFromGallons)
    })),
    metadata: {
      unit: unitSystem,
      long: UnitFormat.displayLongVolumeUnit(unitSystem),
      short: UnitFormat.displayShortVolumeUnit(unitSystem),
      timezone,
    }
  }
}

export function toAreaTemperature(data: MetricsResponse, unitSystem: UnitSystem = UnitSystem.IMPERIAL): number {
  const tempF = data.items.length > 0 ? data.items[data.items.length - 1].averageWeatherTempF : 0;
  return convertMeasure(unitSystem, tempF, UnitConverter.convertFromFahrenheit);
}

export function convertIntervalFromPeriod(period: ConsumptionPeriod): IntervalType {
  switch (period) {
    case ConsumptionPeriod.TODAY:
    case ConsumptionPeriod.LAST_24_HOURS:
      return IntervalType.HOURS;
    case ConsumptionPeriod.THIS_WEEK:
      return IntervalType.DAYS;
    case ConsumptionPeriod.LAST_28_DAYS:
      return IntervalType.WEEKS;
    case ConsumptionPeriod.LAST_12_MONTHS:
      return IntervalType.MONTHS;
    default:
      return IntervalType.HOURS;
  }
}

export function getIntervalFromDateRange(start: string, end: string): IntervalType {
  const diff = moment(end).diff(moment(start), 'days');
  switch (true) {
    case  diff < 3:
      return IntervalType.HOURS;
    case diff >= 3 && diff < 8:
      return IntervalType.DAYS;
    case diff >= 8 && diff < 31:
      return IntervalType.WEEKS;
    case diff >= 31:
      return IntervalType.MONTHS;
    default:
      return IntervalType.HOURS;
  }
}

export function getIntervalValueFromIntervalType(intervalType: IntervalType): IntervalValues {
  switch (intervalType) {
    case IntervalType.HOURS:
      return '1h';
    case IntervalType.DAYS:
    case IntervalType.WEEKS:
      return '1d';
    case IntervalType.MONTHS:
      return '1m';
    default:
      return '1h';
  }
}
